import {authHeaderConfig} from './authHeaderConfig';
import {resolveCompanyHeader} from './companyHeader';
import axios from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import {alertActions} from '../actions/alert.actions';
import {mainActions} from '../actions/main.actions';
import {helpers as servicesHelpers} from '../services/helpers';
import {localStorageKeysConstants} from '../../constants';
import {objectHelpers, scaHelpers, systemHelpers} from '../../utils/helpers';
import {backendEndpoints} from '../../api';

const {COMPANY_ID, PHONE_NUMBER, SESSION_ID, TOKEN} = localStorageKeysConstants;

export const refreshToken = () => {
  const token = JSON.parse(localStorage.getItem(TOKEN));
  const phoneNumber = localStorage.getItem(PHONE_NUMBER);
  const url = request.getRequestUrl(backendEndpoints.GET_AUTH_TOKEN_REFRESH);
  if (!token?.refreshToken) return Promise.resolve(null);
  return axios.post(url, {
    phone_number: atob(phoneNumber),
    refresh_token: token.refreshToken
  })
    .then(res => {
      servicesHelpers.saveJWT(res.data);
      return res.data;
    })
    .catch(err => {
      if (err?.response?.status === 400) {
        servicesHelpers.clearUserStoredData();
        scaHelpers.clearStoredData();
      }
      return Promise.reject(err);
    });
};

const EXPLICIT_DENY_MESSAGE_FRAGMENT = 'explicit deny';

// AWS API Gateway returns 403 with a message containing this phrase when its Lambda authorizer rejects an
// invalid/expired token, so it needs the same refresh-and-retry treatment as a 401. Any other 403 is a real
// permission error. Matched case-insensitively on a fragment rather than the full canned message so it still
// catches the same authorizer rejection if AWS tweaks wording/casing.
// ⚠ The message fragment ALONE cannot tell these apart. API Gateway's authorizer says "explicit
// deny" both for an expired token and for a role that is not allowed to call the route, so matching
// on wording alone classified every permission refusal as an expired token: refresh, retry, denied
// again, then swallowed by disableAlertStatuses. A SUPPORT user clicking "Connect bank" got no
// popup, no error, and one pointless token refresh (H1MVP-4793).
//
// The stored expiry is the missing signal, and it is the client's own fact rather than a guess at
// the authorizer's wording. getAuthDetails().isExpired already reports expired 60s early, so the
// bias is toward still refreshing — a borderline token is treated as expired, not as a permission
// error. A token that is genuinely invalid while the client believes it is fresh (revoked early,
// say) now surfaces as an error rather than silently recovering; that is the intended trade, since
// the alternative hides every real authorization failure in the app.
const isExpiredTokenLike403 = response =>
  response?.status === 403
  && response?.data?.message?.toLowerCase().includes(EXPLICIT_DENY_MESSAGE_FRAGMENT)
  && systemHelpers.getAuthDetails().isExpired;

// Function that will be called to refresh authorization
const refreshAuthLogic = failedRequest => {
  const {status} = failedRequest.response;

  if (status === 403 && !isExpiredTokenLike403(failedRequest.response)) return Promise.reject(failedRequest);

  return refreshToken()
    .then(data => {
      if (data) failedRequest.response.config.headers['Authorization'] = `Bearer ${data.AccessToken}`;
      return Promise.resolve();
    });
}

// Instantiate the interceptor
createAuthRefreshInterceptor(axios, refreshAuthLogic, {statusCodes: [401, 403]});

/**
 * Refresh a token we can already see is expired, BEFORE sending the request that would be rejected.
 *
 * `createAuthRefreshInterceptor` above is reactive: it waits for a 401, then refreshes and retries.
 * That is correct but it costs a guaranteed doomed round trip on every page load, because the stored
 * token has usually aged out since the tab was last open. Measured on a cold `/account`:
 * `GET /api/users/current` came back 401 and was re-sent, before anything could render.
 *
 * The stored token states its own expiry, so we do not need the server to tell us. This reuses
 * `systemHelpers.getAuthDetails().isExpired` — which already treats a token as expired 60s early to
 * cover latency — rather than inventing a second definition of "expired".
 *
 * Three things this must not do:
 *  - refresh the refresh call itself. `refreshToken()` posts through this same interceptor, so
 *    without the URL guard it would wait on a promise that cannot resolve until it is sent.
 *  - fire one refresh per queued request. A cold load dispatches several at once; they share one
 *    in-flight promise instead of stampeding the endpoint.
 *  - break the request when refreshing fails. On failure we fall through and send anyway, so the
 *    reactive 401 path stays in charge of clearing a genuinely dead session.
 */
let inFlightRefresh = null;

const ensureFreshToken = async (config) => {
  if (config.url === request.getRequestUrl(backendEndpoints.GET_AUTH_TOKEN_REFRESH)) return;

  const stored = JSON.parse(localStorage.getItem(TOKEN));
  if (!stored?.refreshToken) return;
  if (!systemHelpers.getAuthDetails().isExpired) return;

  if (!inFlightRefresh) {
    inFlightRefresh = refreshToken()
      .catch(() => null)
      .finally(() => { inFlightRefresh = null; });
  }
  await inFlightRefresh;
};

axios.interceptors.request.use(
  async request => {
    await ensureFreshToken(request);
    // Re-read AFTER the refresh: the whole point is to attach the new token, not the stale one.
    const token = JSON.parse(localStorage.getItem(TOKEN));
    const company = localStorage.getItem(COMPANY_ID);
    const sessionId = localStorage.getItem(SESSION_ID);
    const apiType = request.apiType || false;

    if (token && token.accessToken) request.headers['Authorization'] = `Bearer ${token.accessToken}`;
    if (sessionId) request.headers['SessionId'] = sessionId;
    // 'companyid' for API && 'company-id' for Banking API. A company the caller set wins.
    const companyHeader = resolveCompanyHeader({apiType, storedCompanyId: company, headers: request.headers});
    if (companyHeader) request.headers[companyHeader[0]] = companyHeader[1];
    return request;
  },
  error => Promise.reject(error)
);

const endpoints = {
  ACCOUNTING: process.env.REACT_APP_ACCOUNTING_API_ENDPOINT,
  AGENT: process.env.REACT_APP_AGENT_API_ENDPOINT,
  BACKEND: process.env.REACT_APP_API_ENDPOINT,
  BANKING: process.env.REACT_APP_BANKING_API_ENDPOINT,
  BILLING: process.env.REACT_APP_BILLING_API_ENDPOINT,
  OPEN_BANKING: process.env.REACT_APP_OPEN_BANKING_API_ENDPOINT,
  SERVICE_STATS: process.env.REACT_APP_SERVICE_STATS_API_ENDPOINT
}

// Pulls a human-readable message out of whatever shape the backend used for this error response —
// the APIs behind this app are not consistent about it (description/message/error.message/detail,
// detail as a validation-error array, detail as a nested object). Falls back to the error's own
// string form whenever the response has no data, or the data has none of those keys.
const getErrorMessage = (error) => {
  const data = error?.response?.data;
  if (!data || typeof data !== 'object') return error?.toString?.() ?? String(error);

  if (data.hasOwnProperty('description')) return data.description;
  if (data.hasOwnProperty('message')) return data.message;
  if (data.error?.hasOwnProperty('message')) return data.error.message;
  if (data.hasOwnProperty('detail')) {
    const {detail} = data;
    if (Array.isArray(detail) && detail.length > 0) return detail[0].msg;
    if (typeof detail === 'object' && detail !== null) return JSON.stringify(detail);
    return detail ? detail.toString() : error.toString();
  }

  return error.toString();
};

export const errorHandler = (error, errorFunc, props) => {
  const disableAlert = objectHelpers.getObjProp(props, 'disableAlert', false);
  const hideKeylessError = objectHelpers.getObjProp(props, 'hideKeylessError', false);
  const status = error?.response?.status;

  // 401 stays silent: that is a session problem the refresh interceptor owns and recovers from.
  // 403 does NOT — by the time it reaches here the refresh has already been tried or ruled out, so
  // it is a real refusal, and swallowing it is what made every authorization failure invisible
  // (H1MVP-4793).
  let disableAlertStatuses = [401];
  if (hideKeylessError) disableAlertStatuses = [...disableAlertStatuses, 406, 408];

  const errorMessage = getErrorMessage(error);

  errorFunc?.(error);
  if (!disableAlertStatuses.includes(status) && !disableAlert) alertActions.error(errorMessage);
}

const successHandler = ({result, successCallback, errorHandlerProps} = {}) => {
  const hideKeylessError = objectHelpers.getObjProp(errorHandlerProps, 'hideKeylessError', false);
  let {data} = result;
  if (data && data.hasOwnProperty('errors') && !hideKeylessError){
    alertActions.error(data.errors.toString());
  } else {
    successCallback && successCallback(data);
  }
}

const finallyHandler = ({loading = false, dispatch} = {}) => loading && mainActions.setLoading(false, dispatch)

class Request {
  constructor(endpoint, apiType) {
    this.endpoint = endpoint;
    this.apiType = apiType;
  }
  getAuthHeaderConfig = ({query = {}, skipAuthRefresh = false, ...rest}) => {
    return authHeaderConfig({
      // apiType is carried as a top-level config field (not in params) so it
      // survives the axios-auth-refresh retry: the interceptor must not mutate
      // it away, otherwise the retried request picks the wrong company header.
      params: {...query},
      apiType: this.apiType,
      skipAuthRefresh,
      ...rest
    });
  };
  getRequestUrl = url => `${(this.endpoint || '').replace(/\/$/, '')}${url}`;
  get = ({
     dispatch,
     url,
     query,
     successCallback,
     errorCallback,
     loading = false,
     errorHandlerProps = {},
     headers = {},
     responseType,
     // Runs after the request settles, whichever way it went. `successHandler` calls NEITHER
     // callback when a 200 body carries an `errors` key and `hideKeylessError` is unset, so a
     // caller that only tracks "did one of my two callbacks fire" can wait forever on a request
     // that finished. Anything holding a resolved/pending flag needs this rather than the pair.
     finallyCallback
   }) => {
    if (loading) mainActions.setLoading(true, dispatch);
    axios
      .get(
        this.getRequestUrl(url),
        this.getAuthHeaderConfig({headers, query, responseType})
      )
      .then((result) => successHandler({result, successCallback, errorHandlerProps}))
      .catch((err) => errorHandler(err, errorCallback, errorHandlerProps))
      .finally(() => {
        finallyHandler({loading, dispatch});
        finallyCallback?.();
      });
  }
  post = ({
    dispatch,
    url,
    data,
    successCallback,
    errorCallback,
    loading = false,
    errorHandlerProps = {},
    headers = {},
    skipAuthRefresh,
    // Same contract as `get` above, and the same reason: `successHandler` calls NEITHER
    // callback when a 200 body carries an `errors` key and `hideKeylessError` is unset,
    // so a caller holding a pending flag waits forever on a request that finished.
    finallyCallback
  }) => {
    if (loading) mainActions.setLoading(true, dispatch);
    axios
      .post(
        this.getRequestUrl(url),
        data,
        this.getAuthHeaderConfig({headers, skipAuthRefresh})
      )
      .then((result) => successHandler({result, successCallback, errorHandlerProps}))
      .catch((err) => errorHandler(err, errorCallback, errorHandlerProps))
      .finally(() => {
        finallyHandler({loading, dispatch});
        finallyCallback?.();
      });
  }
  patch = ({
    dispatch,
    url,
    data,
    successCallback,
    errorCallback,
    loading = false,
    errorHandlerProps,
    headers = {},
    skipAuthRefresh
  }) => {
    if (loading) mainActions.setLoading(true, dispatch);
    axios
      .patch(
        this.getRequestUrl(url),
        data,
        this.getAuthHeaderConfig({headers, skipAuthRefresh})
      )
      .then((result) => successHandler({result, successCallback, errorHandlerProps}))
      .catch((err) => errorHandler(err, errorCallback, errorHandlerProps))
      .finally(() => finallyHandler({loading, dispatch}));
  }
  put = ({
    dispatch,
    url,
    data,
    successCallback,
    errorCallback,
    loading = false,
    query = null,
    errorHandlerProps = {},
    headers = {}
  }) => {
    if (loading) mainActions.setLoading(true, dispatch);
    axios
      .put(
        this.getRequestUrl(url),
        data,
        this.getAuthHeaderConfig({headers, query})
      )
      .then((result) => successHandler({result, successCallback, errorHandlerProps}))
      .catch((err) => errorHandler(err, errorCallback, errorHandlerProps))
      .finally(() => finallyHandler({loading, dispatch}));
  }
  delete = ({
    dispatch,
    url,
    successCallback,
    errorCallback,
    loading = false,
    query = null,
    data = undefined,
    headers = {},
    errorHandlerProps = {},
  }) => {
    if (loading) mainActions.setLoading(true, dispatch);
    axios
      .delete(
        this.getRequestUrl(url),
        {...this.getAuthHeaderConfig({headers, query}), data}
      )
      .then((result) => successHandler({result, successCallback, errorHandlerProps}))
      .catch((err) => errorHandler(err, errorCallback, errorHandlerProps))
      .finally(() => finallyHandler({loading, dispatch}));
  }
}

export const request = new Request(endpoints.BACKEND, 'backend');

export const accountingRequest = new Request(endpoints.ACCOUNTING, 'accounting');

export const agentRequest = new Request(endpoints.AGENT, 'agent');

export const bankingRequest = new Request(endpoints.BANKING, 'banking');

export const billingRequest = new Request(endpoints.BILLING, 'billing');

export const openBankingRequest = new Request(endpoints.OPEN_BANKING, 'open_banking');

export const serviceStatsRequest = new Request(endpoints.SERVICE_STATS, 'service_stats');
